import React from 'react';
import { View, Text, StyleSheet, Dimensions, ScrollView } from 'react-native';
import BlankModal from './BlankModal';
import GenericButton from '../molecules/GenericButton';
import { IUserReservation } from '../../types/reservation';

const { width, height } = Dimensions.get('window');

interface WalletRequestSuccessModalProps {
  isVisible: boolean;
  walletReservation: {
    reservation: IUserReservation;
    onboarding_url: string;
  } | null;
  onClose: () => void;
}

const WalletRequestSuccessModal: React.FC<WalletRequestSuccessModalProps> = ({
  isVisible,
  walletReservation,
  onClose
}) => {
  const getOnboardingUrl = () => {
    if (!walletReservation) return '';
    return walletReservation.onboarding_url || '';
  };

  return (
    <BlankModal
      isVisible={isVisible}
      onClose={onClose}
      onConfirm={onClose}
      title="Wallet Request Successful"
      acceptText="Got it">
      <ScrollView
        style={styles.scrollContainer}
        contentContainerStyle={styles.modalContent}
        showsVerticalScrollIndicator={false}
        bounces={true}>
        <Text style={styles.modalSuccessText}>
          🎉 Great! Your wallet request has been submitted successfully.
        </Text>

        {walletReservation && (
          <View style={styles.reservationContainer}>
            <Text style={styles.reservationTitle}>Your Reservation Details:</Text>

            <View style={styles.reservationInfoContainer}>
              <View style={styles.reservationInfoRow}>
                <Text style={styles.reservationInfoLabel}>Reservation ID:</Text>
                <View style={styles.reservationValueContainer}>
                  <Text style={styles.reservationInfoValue}>
                    {walletReservation.reservation.reservation_id}
                  </Text>
                </View>
              </View>

              <View style={styles.reservationInfoRow}>
                <Text style={styles.reservationInfoLabel}>Tenant:</Text>
                <View style={styles.reservationValueContainer}>
                  <Text style={styles.reservationInfoValue}>
                    {walletReservation.reservation.tenant_name}
                  </Text>
                </View>
              </View>
            </View>

            <View style={styles.onboardingSection}>
              <Text style={styles.onboardingTitle}>Next Steps:</Text>
              <Text style={styles.onboardingDescription}>
                A wallet has been reserved for you.{'\n'}
                An onboarding email has been sent to you.{'\n'}
                But to complete your wallet setup right away, please click the button below:
              </Text>

              <GenericButton
                title="Create Wallet"
                onPress={() => {
                  window.location.href = getOnboardingUrl();
                }}
                isPrimary={true}
                buttonStyle={styles.onboardingButton}
              />

              <View style={styles.urlContainer}>
                <Text style={styles.onboardingUrl} selectable={true}>
                  {getOnboardingUrl()}
                </Text>
              </View>

              <Text style={styles.onboardingNote}>
                💡 Tip: Copy this URL and open it in your browser to complete the wallet setup
                process.
              </Text>
            </View>
          </View>
        )}
      </ScrollView>
    </BlankModal>
  );
};

const styles = StyleSheet.create({
  scrollContainer: {
    maxHeight: height * 0.6, // Limit height to prevent overlap
    width: '100%'
  },
  modalContent: {
    alignItems: 'flex-start',
    width: '100%',
    paddingHorizontal: 8,
    paddingBottom: 20, // Add bottom padding to prevent cut-off
    flexGrow: 1
  },
  modalSuccessText: {
    fontSize: width < 350 ? 16 : Math.min(18, width * 0.045),
    fontWeight: 'bold',
    marginBottom: 16,
    color: '#1C274C',
    textAlign: 'center',
    width: '100%',
    lineHeight: width < 350 ? 22 : Math.min(26, width * 0.055)
  },
  reservationContainer: {
    flex: 1,
    width: '100%',
    minHeight: 0
  },
  reservationTitle: {
    fontSize: width < 350 ? 16 : Math.min(18, width * 0.045),
    fontWeight: '600',
    marginBottom: 12,
    color: '#1C274C',
    textAlign: 'center'
  },
  reservationInfoContainer: {
    backgroundColor: '#F9FAFB',
    padding: width < 350 ? 12 : Math.min(16, width * 0.04),
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    width: '100%',
    alignSelf: 'center'
  },
  reservationInfoRow: {
    flexDirection: 'column',
    marginBottom: 12,
    alignItems: 'flex-start'
  },
  reservationInfoLabel: {
    fontSize: width < 350 ? 12 : Math.min(13, width * 0.032),
    fontWeight: '600',
    color: '#374151',
    marginBottom: 4,
    width: '100%'
  },
  reservationValueContainer: {
    width: '100%',
    backgroundColor: '#FFFFFF',
    padding: 8,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#E5E7EB'
  },
  reservationInfoValue: {
    fontSize: width < 350 ? 10 : Math.min(11, width * 0.028),
    color: '#1F2937',
    fontFamily: 'monospace',
    lineHeight: width < 350 ? 14 : Math.min(16, width * 0.04),
    textAlign: 'left',
    width: '100%'
  },
  onboardingSection: {
    marginTop: 16,
    width: '100%',
    alignSelf: 'center'
  },
  onboardingTitle: {
    fontSize: width < 350 ? 14 : Math.min(16, width * 0.04),
    fontWeight: '600',
    marginBottom: 8,
    color: '#1C274C',
    textAlign: 'center'
  },
  onboardingDescription: {
    fontSize: width < 350 ? 13 : Math.min(14, width * 0.035),
    lineHeight: width < 350 ? 18 : Math.min(20, width * 0.05),
    color: '#4B5563',
    textAlign: 'center',
    marginBottom: 12,
    paddingHorizontal: 4
  },
  urlContainer: {
    backgroundColor: '#F3F4F6',
    padding: width < 350 ? 10 : Math.min(12, width * 0.03),
    borderRadius: 8,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#D1D5DB',
    width: '100%',
    minHeight: 40
  },
  onboardingUrl: {
    fontSize: width < 350 ? 9 : Math.min(10, width * 0.025),
    color: '#1F2937',
    fontFamily: 'monospace',
    lineHeight: width < 350 ? 12 : Math.min(14, width * 0.035),
    textAlign: 'left',
    width: '100%'
  },
  onboardingNote: {
    fontSize: width < 350 ? 11 : Math.min(12, width * 0.03),
    color: '#6B7280',
    fontStyle: 'italic',
    lineHeight: width < 350 ? 16 : Math.min(18, width * 0.045),
    textAlign: 'center',
    paddingHorizontal: 4
  },
  onboardingButton: {
    marginTop: 12,
    marginBottom: 12,
    width: '100%',
    alignSelf: 'center'
  }
});

export default WalletRequestSuccessModal;
